import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { apiCall } from '../services/apiUtil';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const UnsubscribeConfirmPage = () => {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('No token provided. Please use the link from your email.');
      return;
    }

    apiCall(`/newsletter/unsubscribe/confirm?token=${encodeURIComponent(token)}`)
      .then(() => {
        setStatus('success');
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.message || 'Invalid or expired unsubscribe link.');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`min-h-screen ${theme.background} flex items-start justify-center pt-8 sm:pt-16 px-4`}>
      <div className={`${theme.card} rounded-xl border ${theme.border} p-6 sm:p-8 max-w-md w-full`}>
        <div className="text-center py-4">

          {status === 'verifying' && (
            <>
              <div className="w-14 h-14 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-7 h-7 text-blue-500 animate-spin" />
              </div>
              <h1 className={`text-xl sm:text-2xl font-bold ${theme.text} mb-2`}>Processing...</h1>
              <p className={`${theme.textMuted} text-sm`}>Confirming your unsubscribe request.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-green-500" />
              </div>
              <h1 className={`text-xl sm:text-2xl font-bold ${theme.text} mb-2`}>You're all set</h1>
              <p className={`${theme.textMuted} text-sm sm:text-base mb-6`}>
                We've removed your email from the Kynaix newsletter. You won't receive any more marketing updates from us.
              </p>
              <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-left`}>
                <p className={`text-xs sm:text-sm ${theme.textMuted}`}>
                  Changed your mind? You can re-subscribe anytime at{' '}
                  <a href="/" className="text-blue-500 hover:text-blue-600 font-medium">kynaixsports.com</a>.
                </p>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-7 h-7 text-red-500" />
              </div>
              <h1 className={`text-xl sm:text-2xl font-bold ${theme.text} mb-2`}>Unable to Unsubscribe</h1>
              <p className={`text-red-500 text-sm sm:text-base mb-6`}>{message}</p>
              <a
                href="/unsubscribe"
                className={`inline-block py-2 px-6 ${theme.secondary} border ${theme.border} ${theme.text} rounded-lg font-medium text-sm hover:opacity-80`}
              >
                Request a New Link
              </a>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default UnsubscribeConfirmPage;
